/**
 * App - Root Application Component
 * 
 * Sets up routing, authentication provider, and layout
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AdminAuthProvider } from './auth/AdminAuthProvider.jsx';
import { AdminLogin } from './auth/AdminLogin.jsx';
import { AdminProtectedRoute } from './auth/AdminProtectedRoute.jsx';
import { AdminLayout } from './layouts/AdminLayout.jsx';

// CRITICAL: Import ALL CSS files first (ensures Vite processes them during build)
// Core styles
import './styles/design-tokens.css';
import './styles/admin.css';
import './styles/layout.css';

// Component styles
import './components/charts/charts.css';
import './components/charts/ActivityTrendChart.css';
import './components/charts/CohortRetentionTable.css';
import './components/charts/EngagementScoreHistogram.css';
import './components/charts/FeatureUsageChart.css';
import './components/charts/RetentionCurveChart.css';
import './components/analytics/CohortAnalysis.css';
import './components/analytics/ComparisonMetrics.css';
import './components/analytics/EngagementMetrics.css';
import './components/analytics/ExportButton.css';
import './components/analytics/LowEngagementUsersList.css';

// New feature pages
import SupportTickets from './pages/SupportTickets.jsx';
import ViewAsUser from './pages/ViewAsUser.jsx';
import ContentManagement from './pages/ContentManagement.jsx';
import FeatureFlags from './pages/FeatureFlags.jsx';
import Monitoring from './pages/Monitoring.jsx';
import Notifications from './pages/Notifications.jsx';
import DataExport from './pages/DataExport.jsx';
import AbuseDetection from './pages/AbuseDetection.jsx';
import AIConfiguration from './pages/AIConfiguration.jsx';
import DocumentManagement from './pages/DocumentManagement.jsx';
import TTSGenerator from './pages/TTSGenerator.jsx';

// Migrated pages
import Dashboard from './pages/Dashboard.jsx';
import UserManagement from './pages/UserManagement.jsx';
import UserDetail from './pages/UserDetail.jsx';
import SubscriptionManagement from './pages/SubscriptionManagement.jsx';
import PaymentDashboard from './pages/PaymentDashboard.jsx';
import PromoCodeDashboard from './pages/PromoCodeDashboard.jsx';
import PromoCodeAnalytics from './pages/PromoCodeAnalytics.jsx';
import PromoCodeUsageSearch from './pages/PromoCodeUsageSearch.jsx';
import WebhookDashboard from './pages/WebhookDashboard.jsx';
import SystemMonitor from './pages/SystemMonitor.jsx';
import AnalyticsDashboard from './pages/AnalyticsDashboard.jsx';
import HealthDashboard from './pages/HealthDashboard.jsx';
import AuditLogViewer from './pages/AuditLogViewer.jsx';
import EmailTracking from './pages/EmailTracking.jsx';
// Page CSS imports
import './pages/AbuseDetection.css';
import './pages/AnalyticsDashboard.css';
import './pages/ContentManagement.css';
import './pages/DataExport.css';
import './pages/FeatureFlags.css';
import './pages/Monitoring.css';
import './pages/Notifications.css';
import './pages/PromoCodeAnalytics.css';
import './pages/PromoCodeDashboard.css';
import './pages/PromoCodeUsageSearch.css';
import './pages/SupportTickets.css';
import './pages/ViewAsUser.css';
import './pages/AIConfiguration.css';
import './pages/DocumentManagement.css';
import './pages/EmailTracking.css';
import './pages/TTSGenerator.css';

export const App = () => {
  return (
    <BrowserRouter>
      <AdminAuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<AdminLogin />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route index element={<Dashboard />} />
            
            {/* User Management */}
            <Route path="users" element={<UserManagement />} />
            <Route path="users/:userId" element={<UserDetail />} />
            <Route path="email-tracking" element={<EmailTracking />} />
            
            {/* Migrated Pages */}
            <Route path="subscriptions" element={<SubscriptionManagement />} />
            <Route path="payments" element={<PaymentDashboard />} />
            <Route path="promo-codes" element={<PromoCodeDashboard />} />
            <Route path="promo-analytics" element={<PromoCodeAnalytics />} />
            <Route path="promo-usage" element={<PromoCodeUsageSearch />} />
            <Route path="webhooks" element={<WebhookDashboard />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="system" element={<SystemMonitor />} />
            <Route path="health" element={<HealthDashboard />} />
            
            {/* New Feature Pages */}
            <Route path="tickets" element={<SupportTickets />} />
            <Route path="content" element={<ContentManagement />} />
            <Route path="feature-flags" element={<FeatureFlags />} />
            <Route path="monitoring" element={<Monitoring />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="exports" element={<DataExport />} />
            <Route path="abuse-detection" element={<AbuseDetection />} />
            <Route path="ai-config" element={<AIConfiguration />} />
            <Route path="documents" element={<DocumentManagement />} />
            <Route path="tts" element={<TTSGenerator />} />
            
            {/* SuperAdmin Only Routes */}
            <Route
              path="audit-logs"
              element={
                <AdminProtectedRoute requireSuperAdmin={true}>
                  <AuditLogViewer />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="view-as-user/:userId"
              element={
                <AdminProtectedRoute requireSuperAdmin={true}>
                  <ViewAsUser />
                </AdminProtectedRoute>
              }
            />
            
            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </AdminAuthProvider>
    </BrowserRouter>
  );
};

const NotFoundPage = () => (
  <div className="admin-card">
    <h2 className="admin-card__title">404 - Page Not Found</h2>
    <p>The page you're looking for doesn't exist.</p>
  </div>
);

export default App;
